import type { ThemeConfig } from 'antd';
import { colors } from './colors';
import { radii } from './radii';
import { spacing } from './spacing';
import { typography } from './typography';
import { motion } from './motion';
import { shadows } from './shadows';
import { boxRadiusComponents } from '../theme/radius';

/**
 * Theme v2 — AntD ThemeConfig assembler.
 *
 * Pulls the raw tokens out of their semantic files and re-maps them
 * onto AntD's flat-key `ThemeConfig` shape. Apps consume this via:
 *
 *   <ConfigProvider theme={libertifyThemeV2}>
 *     <KnowledgeHubExperience />
 *   </ConfigProvider>
 *
 * Scope intentionally narrow — only the components actually used by
 * the Knowledge Hub experience get a `components.*` override entry.
 * Adding more later (Tabs, Drawer, …) is a one-paste change.
 */
export const libertifyThemeV2: ThemeConfig = {
  token: {
    // ── Brand ────────────────────────────────────────────────────────
    colorPrimary: colors.accent.base,
    colorPrimaryHover: colors.accent.hover,
    colorPrimaryActive: colors.accent.active,
    colorPrimaryBg: colors.accent.tint,
    colorPrimaryBorder: colors.accent.border,

    // ── Surfaces ─────────────────────────────────────────────────────
    colorBgContainer: colors.surface.base,
    colorBgLayout: colors.surface.layout,
    colorBgElevated: colors.surface.elevated,

    // ── Text ─────────────────────────────────────────────────────────
    colorText: colors.text.primary,
    colorTextSecondary: colors.text.secondary,
    colorTextTertiary: colors.text.tertiary,
    colorTextQuaternary: colors.text.quaternary,

    // ── Borders ──────────────────────────────────────────────────────
    colorBorder: colors.border.subtle,
    colorBorderSecondary: colors.border.muted,
    colorSplit: colors.border.split,

    // ── Status ───────────────────────────────────────────────────────
    colorSuccess: colors.status.success,
    colorSuccessBg: colors.status.successBg,
    colorError: colors.status.error,
    colorWarning: colors.status.warning,
    colorInfo: colors.status.info,

    // ── Radius ───────────────────────────────────────────────────────
    borderRadius: radii.md,
    borderRadiusLG: radii.lg,
    borderRadiusSM: radii.sm,
    borderRadiusXS: radii.xs,

    // ── Typography ───────────────────────────────────────────────────
    fontFamily: typography.fontFamily,
    fontSize: typography.size.md,
    fontSizeLG: typography.size.lg,
    fontSizeHeading1: typography.size.headingH1,
    fontSizeHeading2: typography.size.headingH2,
    lineHeight: typography.lineHeight.body,

    // ── Spacing ──────────────────────────────────────────────────────
    padding: spacing.base,
    paddingLG: spacing.lg,
    paddingXL: spacing.xl,
    margin: spacing.base,
    marginLG: spacing.lg,

    // ── Motion ───────────────────────────────────────────────────────
    motionDurationMid: motion.duration.base,
    motionDurationSlow: motion.duration.slow,

    // ── Shadow ───────────────────────────────────────────────────────
    boxShadow: shadows.elev1,
    boxShadowSecondary: shadows.elev2,
  },

  components: {
    ...boxRadiusComponents,
    Layout: {
      siderBg: colors.surface.base,
      bodyBg: colors.surface.layout,
      headerBg: colors.surface.base,
      triggerBg: colors.border.muted,
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: colors.accent.tint,
      itemSelectedColor: colors.accent.base,
      itemColor: colors.text.secondary,
      itemHoverColor: colors.accent.base,
      itemHoverBg: '#F8FFFE',
      iconSize: 18,
      iconMarginInlineEnd: spacing.md - 2,
      itemPaddingInline: spacing.md,
      itemHeight: 44,
      borderRadius: radii.md,
    },
    Select: {
      borderRadius: radii.md,
      optionSelectedBg: colors.accent.tint,
      optionSelectedColor: colors.accent.base,
      controlHeight: 36,
    },
    Button: {
      borderRadius: radii.md,
      controlHeight: 40,
      controlHeightSM: 32,
      primaryColor: colors.text.onAccent,
    },
    Progress: {
      colorSuccess: '#86EFAC',
      defaultColor: '#86EFAC',
      remainingColor: colors.border.subtle,
      lineBorderRadius: 100,
    },
    Avatar: {
      colorTextLightSolid: colors.text.onAccent,
    },
  },
};
